import { useState } from 'react';
import { CalendarDays, Clock3, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatDemoDate, getDemoRemainingDays, getDemoStatus, isDemoReadonly, isDemoUser } from '../../utils/demo/demoScope';
import UpgradeContactModal from './UpgradeContactModal';

const quickLinks = [
  { label: 'Deger Zinciri', path: '/deger-zinciri' },
  { label: 'Firsatlar', path: '/firsatlar' },
  { label: 'Uyum Stratejileri', path: '/uyum-stratejileri' },
  { label: 'Sera Gazi', path: '/tsrs-sera-gazi-emisyonlari' },
];

export default function DemoTopBanner() {
  const navigate = useNavigate();
  const { profile, currentFirma } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  if (!isDemoUser(profile)) {
    return null;
  }

  const status = getDemoStatus(profile);
  const remainingDays = getDemoRemainingDays(profile);
  const readonly = isDemoReadonly(profile);
  const expiresAt = formatDemoDate(profile?.demo_expires_at);

  const title = readonly
    ? 'Demo suresi doldu. Hesap salt okunur modda.'
    : status === 'expiring_soon'
      ? 'Demo suresi yakinda dolacak.'
      : 'Demo hesabi aktif.';

  const subtitle = readonly
    ? 'Verileri inceleyebilirsiniz. Yeni kayit icin satis ekibiyle iletisime gecin.'
    : remainingDays !== null
      ? `${remainingDays} gunluk sure icinde strateji, deger zinciri ve firsat modullerini hizlica doldurabilirsiniz.`
      : 'Demo icerigi bu hesap icin aktif edildi.';

  return (
    <>
      <div className="border-b border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50">
        <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3 px-3 py-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-emerald-800">
                <Sparkles className="h-4 w-4" />
                <p className="text-sm font-semibold">{title}</p>
              </div>
              <p className="mt-1 text-xs text-emerald-900/80">{subtitle}</p>

              <div className="mt-3 flex flex-wrap gap-2">
                {quickLinks.map((link) => (
                  <button
                    key={link.path}
                    type="button"
                    onClick={() => navigate(link.path)}
                    className="rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 transition hover:border-emerald-400 hover:bg-emerald-50"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              {expiresAt && (
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Bitis: {expiresAt}
                </span>
              )}
              {(remainingDays !== null || readonly) && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-900">
                  <Clock3 className="h-3.5 w-3.5" />
                  {readonly ? 'Sure doldu' : `${remainingDays} gun`}
                </span>
              )}
              <button
                type="button"
                onClick={() => setIsUpgradeModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-emerald-700"
              >
                <Sparkles className="h-4 w-4" />
                Tam surume gec
              </button>
            </div>
          </div>
        </div>
      </div>
      <UpgradeContactModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        companyName={currentFirma?.firma_adi || profile?.firma_adi || null}
        userName={profile?.tam_adi || null}
        userEmail={profile?.email || null}
        userPhone={profile?.telefon || null}
      />
    </>
  );
}
